import React, { useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { prop, slice, sortBy } from "ramda";

import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TablePagination from "@material-ui/core/TablePagination";

import { mapWithIndex, notBlank } from "../../util/ext";
import { Box, TableFooter } from "@material-ui/core";
import { DateTime } from "luxon";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import ContentIngestionErrorsDialog from "./ContentIngestionErrorsDialog";

const ROWS_PER_PAGE = 20;

const useStyles = makeStyles(theme => ({
  root: {
    width: "calc(100% + 12px)",
    marginLeft: "-6px",
    borderRadius: "20px",
  },

  link: {
    color: theme.palette.primary.brandPrimary,
  },

  tableContainer: {
    height: "calc(100vh - 350px)",
    overflow: "auto",
  },

  pagination: {
    color: theme.palette.primary.darker,
    backgroundColor: "white",
    borderRadius: "20px",
  },
}));

const ChunkStepsTable = props => {
  const { steps, page, onPageChange, onScrollVisible } = props;

  const classes = useStyles();

  //-- Track if the scroll bar is visible
  const tableContainerEl = useRef(null);
  useEffect(() => {
    if (notBlank(tableContainerEl)) {
      const element = tableContainerEl.current;
      const scrollVisible = element.scrollHeight > element.clientHeight;
      onScrollVisible(scrollVisible);
    }
  }, [onScrollVisible]);

  const [errorReportDialogOpen, setErrorReportDialogOpen] = React.useState(
    false,
  );
  const [errorReportLocation, setErrorReportLocation] = React.useState(null);
  const openErrorReport = location => {
    setErrorReportLocation(location);
    setErrorReportDialogOpen(true);
  };

  return (
    <Paper elevation={0} className={classes.root}>
      {notBlank(errorReportLocation) && errorReportDialogOpen && (
        <ContentIngestionErrorsDialog
          errorFileLocation={errorReportLocation}
          open={errorReportDialogOpen}
          onClose={() => setErrorReportDialogOpen(false)}
        />
      )}

      <TableContainer className={classes.tableContainer} ref={tableContainerEl}>
        <Table stickyHeader aria-label="Steps">
          <TableHead>
            <TableRow>
              <TableCell
                key="timestamp"
                align="left"
                style={{ minWidth: 75, maxWidth: 600 }}
              >
                Timestamp
              </TableCell>

              <TableCell
                key="event"
                align="left"
                style={{ minWidth: 75, maxWidth: 600 }}
              >
                Event
              </TableCell>

              <TableCell
                key="task-name"
                align="left"
                style={{ minWidth: 75, maxWidth: 600 }}
              >
                Task Name
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {mapWithIndex(
              (step, idx) => (
                <TableRow hover tabIndex={-1} key={`step-${idx}`}>
                  <TableCell
                    key={"timestamp:" + idx}
                    align="left"
                    style={{ minWidth: 75, maxWidth: 600 }}
                  >
                    {DateTime.fromISO(step.eventTimestamp)
                      .toUTC()
                      .toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS)}
                  </TableCell>

                  <TableCell
                    key={"event:" + idx}
                    align="left"
                    style={{ minWidth: 75, maxWidth: 600 }}
                  >
                    <Box display="flex" alignSelf="center">
                      <Box>{step.eventName}</Box>
                      {notBlank(step.errorDetailsLocation) && (
                        <Box mt={-2.5}>
                          <Tooltip title="View Error Report">
                            <IconButton
                              onClick={() =>
                                openErrorReport(step.errorDetailsLocation)
                              }
                            >
                              <ErrorOutlineIcon
                                color="error"
                                fontSize="large"
                              />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                  </TableCell>

                  <TableCell
                    key={"task-name:" + idx}
                    align="left"
                    style={{ minWidth: 75, maxWidth: 600 }}
                  >
                    {step.taskName}
                  </TableCell>
                </TableRow>
              ),
              slice(
                page * ROWS_PER_PAGE,
                page * ROWS_PER_PAGE + ROWS_PER_PAGE,
                sortBy(prop("eventTimestamp"), steps),
              ),
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                classes={{
                  root: classes.pagination,
                }}
                colSpan={3}
                count={steps.length}
                rowsPerPageOptions={[ROWS_PER_PAGE]}
                rowsPerPage={ROWS_PER_PAGE}
                page={page}
                onChangePage={onPageChange}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default ChunkStepsTable;
